import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from 'app/auth/auth.service';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { environment } from 'environments/environment';

/**
 * Wraps a WebSocket-like object (including SockJS) to track incoming data activity.
 * This allows detection of STOMP heartbeats which are handled internally by the STOMP library.
 */
class ActivityTrackingWebSocket {
  private wrapped: WebSocket;
  private onActivityCallback: () => void;

  constructor(wrapped: WebSocket, onActivityCallback: () => void) {
    this.wrapped = wrapped;
    this.onActivityCallback = onActivityCallback;

    // Proxy all WebSocket properties and methods
    const proxyHandler = {
      get: (target: ActivityTrackingWebSocket, prop: string | symbol) => {
        if (prop === 'onmessage') {
          return target.wrapped.onmessage;
        }
        const value = (target.wrapped as unknown as Record<string | symbol, unknown>)[prop];
        if (typeof value === 'function') {
          return value.bind(target.wrapped);
        }
        return value;
      },
      set: (target: ActivityTrackingWebSocket, prop: string | symbol, value: unknown) => {
        if (prop === 'onmessage') {
          // Wrap onmessage to track activity
          target.wrapped.onmessage = (event: MessageEvent) => {
            target.onActivityCallback();
            if (typeof value === 'function') {
              (value as (event: MessageEvent) => void)(event);
            }
          };
        } else {
          (target.wrapped as unknown as Record<string | symbol, unknown>)[prop] = value;
        }
        return true;
      }
    };

    return new Proxy(this, proxyHandler) as unknown as ActivityTrackingWebSocket;
  }
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly authService = inject(AuthService);
  private stompClient!: Client;

  private _connected = signal(false);
  readonly connected = this._connected.asReadonly();

  private pingIntervalId: number | null = null;
  private watchdogIntervalId: number | null = null;
  private lastActivityTimestamp: number = 0;
  private isReconnecting = false;
  private isInitializing = false;
  private visibilityHandler: (() => void) | null = null;

  // Connection is considered stale if no activity for this duration (75 seconds)
  // This should be slightly longer than the server heartbeat interval (60s) to allow
  // for network latency while still detecting dead connections quickly
  private readonly STALE_CONNECTION_THRESHOLD_MS = 75000;

  constructor() {
    this.setupVisibilityHandler();
  }

  /**
   * Set up Page Visibility API handler to reconnect when tab becomes active.
   * This handles the case where browsers (especially Safari) throttle/suspend
   * background tabs, causing the WebSocket connection to silently die.
   */
  private setupVisibilityHandler(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const token = this.authService.token();
        if (token && !this._connected() && !this.isInitializing && !this.isReconnecting) {
          // Tab became visible and we're not connected - reconnect
          this.initSocketConnection();
        } else if (token && this._connected()) {
          // Tab became visible - check if connection is stale
          if (this.isConnectionStale()) {
            // Connection is stale, force reconnection
            this.forceReconnect();
          } else {
            // Connection seems ok - verify with a ping
            // If the connection is dead, the ping will fail and trigger reconnection
            this.send('/app/chat.ping', { ts: Date.now() });
          }
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Check if the connection is stale (no activity for too long).
   * Safari can silently kill connections without triggering close events.
   */
  private isConnectionStale(): boolean {
    if (this.lastActivityTimestamp === 0) {
      return false;
    }
    return Date.now() - this.lastActivityTimestamp > this.STALE_CONNECTION_THRESHOLD_MS;
  }

  /**
   * Record activity timestamp when any message is received or sent successfully.
   */
  private recordActivity(): void {
    this.lastActivityTimestamp = Date.now();
  }

  /**
   * Force reconnection by closing the current connection and reinitializing.
   */
  private forceReconnect(): void {
    if (this.isReconnecting || this.isInitializing) {
      return;
    }

    this._connected.set(false);
    this.stopManualPing();
    this.stopWatchdog();

    if (this.stompClient) {
      try {
        this.stompClient.deactivate();
      } catch {
        // Ignore errors if already disconnected
      }
    }

    // Small delay before reconnecting (100ms to prevent rapid reconnection loops)
    this.isReconnecting = true;
    setTimeout(() => {
      this.isReconnecting = false;
      this.initSocketConnection();
    }, 100);
  }

  /**
   * Start a watchdog timer that periodically checks for stale connections.
   * This catches cases where Safari silently kills the connection without events.
   */
  private startWatchdog(): void {
    this.stopWatchdog();

    // Check every 15 seconds for stale connection
    this.watchdogIntervalId = window.setInterval(() => {
      if (this._connected() && this.isConnectionStale()) {
        this.forceReconnect();
      }
    }, 15000);
  }

  private stopWatchdog(): void {
    if (this.watchdogIntervalId !== null) {
      clearInterval(this.watchdogIntervalId);
      this.watchdogIntervalId = null;
    }
  }

  initSocketConnection(): void {
    const token = this.authService.token();
    if (!token) {
      return;
    }

    // Prevent multiple simultaneous initialization attempts
    if (this.isInitializing) {
      return;
    }

    // If already connected, don't reinitialize
    if (this._connected()) {
      return;
    }

    this.isInitializing = true;

    // Clean up any existing connection before creating a new one
    if (this.stompClient) {
      try {
        this.stompClient.deactivate();
      } catch {
        // Ignore errors if already disconnected
      }
    }
    this._connected.set(false);
    this.stopManualPing();

    this.stompClient = new Client({
      webSocketFactory: () => {
        // Get fresh token on every connection attempt (including reconnections)
        const currentToken = this.authService.token();
        const sockJs = new SockJS(`${environment.socketBaseUrl}?token=${currentToken}`);
        // Wrap the WebSocket to track incoming activity (including STOMP heartbeats)
        return new ActivityTrackingWebSocket(sockJs as unknown as WebSocket, () => this.recordActivity()) as unknown as WebSocket;
      },

      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      debug: () => {},
      reconnectDelay: 0, // Disable automatic reconnection - we'll handle it manually with fresh tokens
      heartbeatIncoming: 60000,
      heartbeatOutgoing: 20000,

      onConnect: () => {
        this._connected.set(true);
        this.isInitializing = false;
        this.isReconnecting = false;
        this.recordActivity();
        this.startManualPing();
        this.startWatchdog();
      },

      onDisconnect: () => {
        this._connected.set(false);
        this.isInitializing = false;
        this.stopManualPing();
        this.stopWatchdog();
      },

      onStompError: () => {
        this.isInitializing = false;
      },

      onWebSocketClose: () => {
        // Always mark as disconnected when WebSocket closes
        this._connected.set(false);
        this.stopManualPing();
        this.stopWatchdog();

        // Prevent reconnection loop - only reconnect if not already reconnecting
        if (this.isReconnecting) {
          return;
        }

        // Manual reconnection with fresh token after 5 seconds
        this.isReconnecting = true;
        setTimeout(() => {
          const currentToken = this.authService.token();
          if (currentToken && !this._connected()) {
            this.isReconnecting = false;
            this.initSocketConnection();
          } else {
            this.isReconnecting = false;
          }
        }, 5000);
      },
    });

    this.stompClient.activate();
  }

  startManualPing(intervalMs = 20000): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
    }

    this.pingIntervalId = window.setInterval(() => {
      if (this._connected()) {
        this.send('/app/chat.ping', { ts: Date.now() });
      }
    }, intervalMs);
  }

  stopManualPing(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  send(destination: string, payload: object = {}, retryCount = 10): void {
    // Check for stale connection BEFORE attempting to send
    // This catches zombie connections where _connected is true but the socket is dead
    if (this._connected() && this.isConnectionStale()) {
      console.warn('[WebSocket] Stale connection detected on send, forcing reconnect');
      this.forceReconnect();
      // Fall through to retry logic below
    }

    if (!this._connected()) {
      if (retryCount <= 0) {
        console.warn('[WebSocket] Message send failed after all retries');
        return;
      }

      setTimeout(() => {
        this.send(destination, payload, retryCount - 1);
      }, 1000);

      return;
    }

    try {
      this.stompClient.publish({
        destination,
        body: JSON.stringify(payload),
      });
      this.recordActivity(); // Track successful send
    } catch {
      // Mark as disconnected if send fails
      console.warn('[WebSocket] Send failed, marking as disconnected');
      this._connected.set(false);
    }
  }

  /**
   * Send a message with callback support for success/failure feedback.
   * Returns a Promise that resolves on success or rejects on failure.
   * On failure, triggers reconnection automatically.
   */
  sendWithCallback(destination: string, payload: object = {}): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check for stale connection BEFORE attempting to send
      if (this._connected() && this.isConnectionStale()) {
        console.warn('[WebSocket] Stale connection detected on sendWithCallback, forcing reconnect');
        this.forceReconnect();
        reject(new Error('WebSocket connection stale, reconnecting'));
        return;
      }

      if (!this._connected()) {
        // Try to reconnect
        this.forceReconnect();
        reject(new Error('WebSocket not connected'));
        return;
      }

      try {
        this.stompClient.publish({
          destination,
          body: JSON.stringify(payload),
        });
        this.recordActivity();
        resolve();
      } catch (error) {
        // Mark as disconnected and trigger reconnection
        console.warn('[WebSocket] sendWithCallback failed, marking as disconnected');
        this._connected.set(false);
        this.forceReconnect();
        reject(error);
      }
    });
  }

  subscribe(
    destination: string,
    callback: (msg: IMessage) => void
  ): StompSubscription | undefined {
    if (!this._connected()) {
      return undefined;
    }

    // Wrap the callback to track activity on every received message
    return this.stompClient.subscribe(destination, (msg: IMessage) => {
      this.recordActivity();
      callback(msg);
    });
  }

  disconnect(): void {
    // Prevent auto-reconnection when manually disconnecting
    this.isReconnecting = true;

    // Always try to deactivate, regardless of connection status
    if (this.stompClient) {
      try {
        this.stompClient.deactivate();
      } catch {
        // Ignore errors
      }
    }
    this._connected.set(false);
    this.stopManualPing();
    this.stopWatchdog();

    // Reset reconnecting flag after a delay (in case user logs in again)
    setTimeout(() => {
      this.isReconnecting = false;
    }, 1000);
  }
}
